/**
 * Browser `PushManager` plumbing (F22). Isolated from `usePushSettings.ts`
 * so the state machine there is unit-testable without a jsdom
 * `ServiceWorkerRegistration`/`PushManager` shim in every test.
 */

/** Converts a VAPID base64url public key into the `Uint8Array` `applicationServerKey` expects. */
export function urlBase64ToUint8Array(base64Url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export type PushSupport = 'supported' | 'unsupported';

/**
 * Whether this browser can even attempt push (Notification + SW +
 * PushManager APIs present). Checks truthiness, not just `in` — a test (or
 * a browser) can legitimately have the property present but `undefined`.
 */
export function detectPushSupport(): PushSupport {
  if (typeof window === 'undefined') return 'unsupported';
  const supported = Boolean(navigator.serviceWorker) && Boolean(window.PushManager) && Boolean(window.Notification);
  return supported ? 'supported' : 'unsupported';
}

/** Just the SW + PushManager presence check — used by the read/unsubscribe paths below, which don't need the Notification API at all (no permission prompt involved). */
function hasServiceWorkerAndPushManager(): boolean {
  return typeof window !== 'undefined' && Boolean(navigator.serviceWorker) && Boolean(window.PushManager);
}

export interface SubscriptionKeys {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

function toSubscriptionKeys(subscription: PushSubscription): SubscriptionKeys {
  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  if (!p256dh || !auth) {
    throw new Error('pushSubscription: browser PushSubscription is missing p256dh/auth keys');
  }
  const toB64 = (buf: ArrayBuffer): string => btoa(String.fromCharCode(...new Uint8Array(buf)));
  return { endpoint: subscription.endpoint, keys: { p256dh: toB64(p256dh), auth: toB64(auth) } };
}

/**
 * Reads an already-active subscription for the given SW registration, if
 * any, WITHOUT prompting for permission (ux.md: "never nag on load").
 * Returns `undefined` when there's no active subscription, no registration
 * ready, or the SW/Push APIs aren't supported.
 */
export async function getExistingSubscription(): Promise<SubscriptionKeys | undefined> {
  if (!hasServiceWorkerAndPushManager()) return undefined;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return undefined;
  const subscription = await registration.pushManager.getSubscription();
  return subscription ? toSubscriptionKeys(subscription) : undefined;
}

export class PushSubscribeError extends Error {
  constructor(
    message: string,
    readonly reason: 'unsupported' | 'no-registration' | 'permission-denied' | 'subscribe-failed',
  ) {
    super(message);
    this.name = 'PushSubscribeError';
  }
}

/**
 * The explicit, user-initiated opt-in flow (ux.md: never auto-prompted).
 * Requests Notification permission (browser-native prompt — the ONE
 * permission dialog this whole feature ever triggers, and only on click)
 * then subscribes via the active SW registration's `PushManager`.
 *
 * `no-registration` fires when this dev environment hasn't registered a
 * service worker yet (`src/pwa/registerServiceWorker.ts` is PROD-ONLY by
 * design, E9) — the caller shows a clear "not ready in this session"
 * message rather than hanging on `navigator.serviceWorker.ready`, which
 * never resolves without an active registration.
 */
export async function subscribeToPush(vapidPublicKey: string): Promise<SubscriptionKeys> {
  if (detectPushSupport() === 'unsupported') {
    throw new PushSubscribeError('Push is not supported in this browser', 'unsupported');
  }

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) {
    throw new PushSubscribeError('No active service worker registration', 'no-registration');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new PushSubscribeError('Notification permission was not granted', 'permission-denied');
  }

  try {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      // `lib.dom.d.ts`'s `BufferSource` wants an `ArrayBufferView<ArrayBuffer>`
      // specifically; `Uint8Array`'s backing-buffer type is generic
      // (`ArrayBufferLike`, which also covers `SharedArrayBuffer`) as of
      // recent TS DOM lib typings. `urlBase64ToUint8Array` always constructs
      // a plain (non-shared) `ArrayBuffer`-backed array, so this is a type
      // widening the runtime value already satisfies, not an unsafe cast.
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
    });
    return toSubscriptionKeys(subscription);
  } catch (err) {
    throw new PushSubscribeError(
      `PushManager.subscribe failed: ${err instanceof Error ? err.message : String(err)}`,
      'subscribe-failed',
    );
  }
}

/** Unsubscribes the active browser subscription, if any. Idempotent — returns `false` when there was nothing to unsubscribe. */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!hasServiceWorkerAndPushManager()) return false;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return false;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return false;
  return subscription.unsubscribe();
}
